import {
  SURVEY_LAYER_CLASSIFICATION_KEYS,
  SURVEY_LAYER_CLASSIFICATIONS,
  SURVEY_LAYER_DATA_RANGES,
  SURVEY_LAYER_SOIL_TYPE_COLORS,
  SURVEY_LAYERS,
  SURVEY_SOIL_CLASS_LAYERS,
} from '../../../constants/SurveyLayers';

const soilTypesFI = require('../locales/soil-types-intervals-fi.json');
const soilTypesEE = require('../locales/soil-types-intervals-ee.json');
const soilTypesLT = require('../locales/soil-types-intervals-lt.json');

export const COUNTRY_CODES = Object.freeze({
  DENMARK: 'DK',
  SWEDEN: 'SE',
  ESTONIA: 'EE',
  FINLAND: 'FI',
  LITHUANIA: 'LT',
});

export const DEMAND_TYPES = Object.freeze({
  FI: 'FI_DEMAND',
  EE: 'EE_DEMAND',
  DK: 'DK_DEMAND',
  SE: 'SE_DEMAND',
  LT: 'LT_DEMAND',
});

export const SWEDEN_ONLY_SURVEY_LAYERS = Object.freeze([
  SURVEY_LAYERS.COPPER_HCL,
  SURVEY_LAYERS.MAGNESIUM_AL,
  SURVEY_LAYERS.PHOSPHORUS_AL,
  SURVEY_LAYERS.PHOSPHORUS_HCL,
  SURVEY_LAYERS.POTASSIUM_AL,
  SURVEY_LAYERS.POTASSIUM_HCL,
  SURVEY_LAYERS.POTASSIUM_MG,
  SURVEY_LAYERS.CALCIUM_AL,
]);

const COUNTRY_NAME_TO_CODE = Object.freeze({
  DENMARK: COUNTRY_CODES.DENMARK,
  SWEDEN: COUNTRY_CODES.SWEDEN,
  ESTONIA: COUNTRY_CODES.ESTONIA,
  FINLAND: COUNTRY_CODES.FINLAND,
  LITHUANIA: COUNTRY_CODES.LITHUANIA,
  DK: COUNTRY_CODES.DENMARK,
  SE: COUNTRY_CODES.SWEDEN,
  EE: COUNTRY_CODES.ESTONIA,
  FI: COUNTRY_CODES.FINLAND,
  LT: COUNTRY_CODES.LITHUANIA,
});

let activeSurveyCountryCode = null;

const cloneArray = (arr) => (Array.isArray(arr) ? [...arr] : arr);

const cloneClassificationMap = (map) =>
  Object.keys(map || {}).reduce((acc, key) => {
    const ranges = map[key];
    acc[key] = Object.keys(ranges || {}).reduce((inner, classification) => {
      inner[classification] = cloneArray(ranges[classification]);
      return inner;
    }, {});
    return acc;
  }, {});

const filterByAllowedLayers = (source, disabledLayers) => {
  return Object.keys(source || {}).reduce((acc, layer) => {
    if (!disabledLayers.includes(layer)) {
      acc[layer] = source[layer];
    }
    return acc;
  }, {});
};

const mergeDataRangeAdjustments = (base, adjustments) => {
  const result = { ...base };
  Object.keys(adjustments || {}).forEach((layer) => {
    result[layer] = cloneArray(adjustments[layer]);
  });
  return result;
};

const mergeClassificationAdjustments = (base, adjustments) => {
  const result = cloneClassificationMap(base);
  Object.keys(adjustments || {}).forEach((layer) => {
    const ranges = adjustments[layer] || {};
    result[layer] = Object.keys(ranges).reduce((inner, classification) => {
      inner[classification] = cloneArray(ranges[classification]);
      return inner;
    }, {});
  });
  return result;
};

const mapDiscreteIntervalsByColor = (baseIntervals, ranges) => {
  const colors = Object.keys(baseIntervals || {});
  if (!Array.isArray(ranges) || ranges.length !== colors.length) {
    return baseIntervals;
  }

  return colors.reduce((acc, color, index) => {
    acc[color] = cloneArray(ranges[index]);
    return acc;
  }, {});
};

const COUNTRY_DISABLED_LAYERS = Object.freeze({
  [COUNTRY_CODES.DENMARK]: [
    SURVEY_LAYERS.FI_SOIL_CLASS,
    SURVEY_LAYERS.EE_SOIL_CLASS,
    SURVEY_LAYERS.OM,
    SURVEY_LAYERS.CEC,
    SURVEY_LAYERS.ORGANIC_CARBON,
    ...SWEDEN_ONLY_SURVEY_LAYERS,
  ],
  [COUNTRY_CODES.SWEDEN]: [
    SURVEY_LAYERS.JB,
    SURVEY_LAYERS.FI_SOIL_CLASS,
    SURVEY_LAYERS.EE_SOIL_CLASS,
    SURVEY_LAYERS.OM,
    SURVEY_LAYERS.CEC,
    SURVEY_LAYERS.ORGANIC_CARBON,
  ],
  [COUNTRY_CODES.ESTONIA]: [
    SURVEY_LAYERS.JB,
    SURVEY_LAYERS.FI_SOIL_CLASS,
    ...SWEDEN_ONLY_SURVEY_LAYERS,
  ],
  [COUNTRY_CODES.FINLAND]: [
    SURVEY_LAYERS.JB,
    SURVEY_LAYERS.EE_SOIL_CLASS,
    SURVEY_LAYERS.OM,
    SURVEY_LAYERS.CEC,
    SURVEY_LAYERS.ORGANIC_CARBON,
    ...SWEDEN_ONLY_SURVEY_LAYERS,
  ],
  [COUNTRY_CODES.LITHUANIA]: [
    SURVEY_LAYERS.JB,
    SURVEY_LAYERS.FI_SOIL_CLASS,
    SURVEY_LAYERS.EE_SOIL_CLASS,
    ...SWEDEN_ONLY_SURVEY_LAYERS,
  ],
});

// Country-specific static baselines. Populate these when ranges/classifications differ from defaults.
export const COUNTRY_LAYER_DATA_RANGE_ADJUSTMENTS = Object.freeze({
  [COUNTRY_CODES.DENMARK]: {
    [SURVEY_LAYERS.RT]: [0, 8],
    [SURVEY_LAYERS.PHOSPHORUS]: [0, 6],
    [SURVEY_LAYERS.POTASSIUM]: [0, 15],
    [SURVEY_LAYERS.MAGNESIUM]: [0, 10],
    [SURVEY_LAYERS.COPPER]: [0, 5],
    [SURVEY_LAYERS.BORON]: [0, 8],
    [SURVEY_LAYERS.ZINC]: [0, 3],
    [SURVEY_LAYERS.FINE_SAND]: [0, 100],
    [SURVEY_LAYERS.COARSE_SAND]: [0, 100],
    [SURVEY_LAYERS.SAND]: [0, 100],
    [SURVEY_LAYERS.SILT]: [0, 100],
    [SURVEY_LAYERS.CLAY]: [0, 45],
    [SURVEY_LAYERS.HUMUS]: [0, 30],
  },
  [COUNTRY_CODES.SWEDEN]: {
    [SURVEY_LAYERS.PH]: [0, 8],
    [SURVEY_LAYERS.PHOSPHORUS_AL]: [0, 16],
    [SURVEY_LAYERS.POTASSIUM_AL]: [0, 32],
    [SURVEY_LAYERS.MAGNESIUM_AL]: [0, 15],
    [SURVEY_LAYERS.COPPER_HCL]: [0, 8],
    [SURVEY_LAYERS.BORON]: [0, 1],
    [SURVEY_LAYERS.CALCIUM_AL]: [0, 250],
    [SURVEY_LAYERS.PHOSPHORUS_HCL]: [0, 120],
    [SURVEY_LAYERS.POTASSIUM_HCL]: [0, 550],
    [SURVEY_LAYERS.POTASSIUM_MG]: [0, 5],
    [SURVEY_LAYERS.SAND]: [0, 100],
    [SURVEY_LAYERS.CLAY]: [2, 80],
    [SURVEY_LAYERS.HUMUS]: [0, 40],
  },
  [COUNTRY_CODES.ESTONIA]: {
    [SURVEY_LAYERS.PH]: [0, 8],
    [SURVEY_LAYERS.PHOSPHORUS]: [0, 300],
    [SURVEY_LAYERS.POTASSIUM]: [0, 700],
    [SURVEY_LAYERS.MAGNESIUM]: [0, 400],
    [SURVEY_LAYERS.MANGANESE]: [0, 200],
    [SURVEY_LAYERS.BORON]: [0, 5],
    [SURVEY_LAYERS.SAND]: [0, 100],
    [SURVEY_LAYERS.SILT]: [0, 100],
    [SURVEY_LAYERS.CLAY]: [2, 45],
    [SURVEY_LAYERS.HUMUS]: [0, 30],
    [SURVEY_LAYERS.OM]: [0, 30],
    [SURVEY_LAYERS.CEC]: [0, 5],
    [SURVEY_LAYERS.ORGANIC_CARBON]: [0, 30],
    [SURVEY_LAYERS.ZINC]: [0, 2],
    [SURVEY_LAYERS.SULFUR]: [0, 50],
    [SURVEY_LAYERS.MOLYBDENUM]: [0, 0.1],
  },
  [COUNTRY_CODES.FINLAND]: {},
  [COUNTRY_CODES.LITHUANIA]: {
    [SURVEY_LAYERS.PH]: [0, 8],
    [SURVEY_LAYERS.PHOSPHORUS]: [0, 300],
    [SURVEY_LAYERS.POTASSIUM]: [0, 700],
    [SURVEY_LAYERS.MAGNESIUM]: [0, 400],
    [SURVEY_LAYERS.MANGANESE]: [0, 200],
    [SURVEY_LAYERS.BORON]: [0, 5],
    [SURVEY_LAYERS.SAND]: [0, 100],
    [SURVEY_LAYERS.SILT]: [0, 100],
    [SURVEY_LAYERS.CLAY]: [2, 45],
    [SURVEY_LAYERS.HUMUS]: [0, 30],
    [SURVEY_LAYERS.OM]: [0, 30],
    [SURVEY_LAYERS.CEC]: [0, 5],
    [SURVEY_LAYERS.ORGANIC_CARBON]: [0, 30],
    [SURVEY_LAYERS.ZINC]: [0, 2],
    [SURVEY_LAYERS.SULFUR]: [0, 50],
    [SURVEY_LAYERS.MOLYBDENUM]: [0, 0.1],
  },
});

export const COUNTRY_LAYER_CLASSIFICATION_ADJUSTMENTS = Object.freeze({
  [COUNTRY_CODES.DENMARK]: {
    [SURVEY_LAYERS.RT]: {
      [SURVEY_LAYER_CLASSIFICATION_KEYS.DEMAND_LARGE]: [1.5, Number.MAX_VALUE],
      [SURVEY_LAYER_CLASSIFICATION_KEYS.DEMAND_MEDIUM]: [0.9, 1.5],
      [SURVEY_LAYER_CLASSIFICATION_KEYS.DEMAND_SMALL]: [0.25, 0.9],
      [SURVEY_LAYER_CLASSIFICATION_KEYS.DEMAND_OK]: [-0.25, 0.25],
      [SURVEY_LAYER_CLASSIFICATION_KEYS.SURPLUS_SMALL]: [-0.9, -0.25],
      [SURVEY_LAYER_CLASSIFICATION_KEYS.SURPLUS_LARGE]: [-1.5, -0.9],
      [SURVEY_LAYER_CLASSIFICATION_KEYS.SURPLUS_SUSPICIOUS]: [-Number.MAX_VALUE, -1.5],
    },
    [SURVEY_LAYERS.MAGNESIUM]: {
      [SURVEY_LAYER_CLASSIFICATION_KEYS.DEMAND_LARGE]: [0.0, 2.0],
      [SURVEY_LAYER_CLASSIFICATION_KEYS.DEMAND_MEDIUM]: [2.0, 3.0],
      [SURVEY_LAYER_CLASSIFICATION_KEYS.DEMAND_SMALL]: [3.0, 4.0],
      [SURVEY_LAYER_CLASSIFICATION_KEYS.DEMAND_OK]: [4.0, 6.0],
      [SURVEY_LAYER_CLASSIFICATION_KEYS.SURPLUS_SMALL]: [6.0, 8.0],
      [SURVEY_LAYER_CLASSIFICATION_KEYS.SURPLUS_LARGE]: [8.0, 10.0],
      [SURVEY_LAYER_CLASSIFICATION_KEYS.SURPLUS_SUSPICIOUS]: [10.0, Number.MAX_VALUE],
    },
    [SURVEY_LAYERS.COPPER]: {
      [SURVEY_LAYER_CLASSIFICATION_KEYS.DEMAND_LARGE]: [0.0, 1.0],
      [SURVEY_LAYER_CLASSIFICATION_KEYS.DEMAND_MEDIUM]: [1.0, 1.5],
      [SURVEY_LAYER_CLASSIFICATION_KEYS.DEMAND_SMALL]: [1.5, 2.0],
      [SURVEY_LAYER_CLASSIFICATION_KEYS.DEMAND_OK]: [2.0, 3.0],
      [SURVEY_LAYER_CLASSIFICATION_KEYS.SURPLUS_SMALL]: [3.0, 4.0],
      [SURVEY_LAYER_CLASSIFICATION_KEYS.SURPLUS_LARGE]: [4.0, 5.0],
      [SURVEY_LAYER_CLASSIFICATION_KEYS.SURPLUS_SUSPICIOUS]: [5.0, Number.MAX_VALUE],
    },
    [SURVEY_LAYERS.BORON]: {
      [SURVEY_LAYER_CLASSIFICATION_KEYS.DEMAND_LARGE]: [0.0, 1.5],
      [SURVEY_LAYER_CLASSIFICATION_KEYS.DEMAND_MEDIUM]: [1.5, 2.5],
      [SURVEY_LAYER_CLASSIFICATION_KEYS.DEMAND_SMALL]: [2.5, 3.0],
      [SURVEY_LAYER_CLASSIFICATION_KEYS.DEMAND_OK]: [3.0, 5.0],
      [SURVEY_LAYER_CLASSIFICATION_KEYS.SURPLUS_SMALL]: [5.0, 6.5],
      [SURVEY_LAYER_CLASSIFICATION_KEYS.SURPLUS_LARGE]: [6.5, 8.0],
      [SURVEY_LAYER_CLASSIFICATION_KEYS.SURPLUS_SUSPICIOUS]: [8.0, Number.MAX_VALUE],
    },
    [SURVEY_LAYERS.ZINC]: {
      [SURVEY_LAYER_CLASSIFICATION_KEYS.DEMAND_LARGE]: [0.0, 1.0],
      [SURVEY_LAYER_CLASSIFICATION_KEYS.DEMAND_OK]: [1.0, 3.0],
      [SURVEY_LAYER_CLASSIFICATION_KEYS.SURPLUS_SUSPICIOUS]: [3.0, Number.MAX_VALUE],
    },
  },
  [COUNTRY_CODES.SWEDEN]: {
    [SURVEY_LAYERS.PH]: {
      [SURVEY_LAYER_CLASSIFICATION_KEYS.DEMAND_LARGE]: [0.0, 4.5],
      [SURVEY_LAYER_CLASSIFICATION_KEYS.DEMAND_MEDIUM]: [4.5, 5.0],
      [SURVEY_LAYER_CLASSIFICATION_KEYS.DEMAND_SMALL]: [5.0, 6.0],
      [SURVEY_LAYER_CLASSIFICATION_KEYS.DEMAND_OK]: [6.0, 6.5],
      [SURVEY_LAYER_CLASSIFICATION_KEYS.SURPLUS_SMALL]: [6.5, 7.0],
      [SURVEY_LAYER_CLASSIFICATION_KEYS.SURPLUS_LARGE]: [7.0, 8.0],
      [SURVEY_LAYER_CLASSIFICATION_KEYS.SURPLUS_SUSPICIOUS]: [8.0, Number.MAX_VALUE],
    },
    [SURVEY_LAYERS.PHOSPHORUS_AL]: {
      [SURVEY_LAYER_CLASSIFICATION_KEYS.DEMAND_LARGE]: [0.0, 2.0],
      [SURVEY_LAYER_CLASSIFICATION_KEYS.DEMAND_MEDIUM]: [2.0, 4.0],
      [SURVEY_LAYER_CLASSIFICATION_KEYS.DEMAND_SMALL]: [4.0, 8.0],
      [SURVEY_LAYER_CLASSIFICATION_KEYS.DEMAND_OK]: [8.0, 12.0],
      [SURVEY_LAYER_CLASSIFICATION_KEYS.SURPLUS_SMALL]: [12.0, 16.0],
      [SURVEY_LAYER_CLASSIFICATION_KEYS.SURPLUS_LARGE]: [16.0, Number.MAX_VALUE],
    },
    [SURVEY_LAYERS.POTASSIUM_AL]: {
      [SURVEY_LAYER_CLASSIFICATION_KEYS.DEMAND_LARGE]: [0.0, 4.0],
      [SURVEY_LAYER_CLASSIFICATION_KEYS.DEMAND_MEDIUM]: [4.0, 8.0],
      [SURVEY_LAYER_CLASSIFICATION_KEYS.DEMAND_SMALL]: [8.0, 16.0],
      [SURVEY_LAYER_CLASSIFICATION_KEYS.DEMAND_OK]: [16.0, 32.0],
      [SURVEY_LAYER_CLASSIFICATION_KEYS.SURPLUS_SMALL]: [32.0, Number.MAX_VALUE],
    },
    [SURVEY_LAYERS.MAGNESIUM_AL]: {
      [SURVEY_LAYER_CLASSIFICATION_KEYS.DEMAND_LARGE]: [0.0, 2.0],
      [SURVEY_LAYER_CLASSIFICATION_KEYS.DEMAND_MEDIUM]: [2.0, 6.0],
      [SURVEY_LAYER_CLASSIFICATION_KEYS.DEMAND_SMALL]: [6.0, 10.0],
      [SURVEY_LAYER_CLASSIFICATION_KEYS.DEMAND_OK]: [10.0, 15.0],
      [SURVEY_LAYER_CLASSIFICATION_KEYS.SURPLUS_SMALL]: [15.0, Number.MAX_VALUE],
    },
    [SURVEY_LAYERS.COPPER_HCL]: {
      [SURVEY_LAYER_CLASSIFICATION_KEYS.DEMAND_LARGE]: [0.0, 4.0],
      [SURVEY_LAYER_CLASSIFICATION_KEYS.DEMAND_MEDIUM]: [4.0, 6.0],
      [SURVEY_LAYER_CLASSIFICATION_KEYS.DEMAND_SMALL]: [6.0, 8.0],
      [SURVEY_LAYER_CLASSIFICATION_KEYS.DEMAND_OK]: [8.0, Number.MAX_VALUE],
    },
    [SURVEY_LAYERS.BORON]: {
      [SURVEY_LAYER_CLASSIFICATION_KEYS.DEMAND_LARGE]: [0.0, 0.5],
      [SURVEY_LAYER_CLASSIFICATION_KEYS.DEMAND_MEDIUM]: [0.5, 0.8],
      [SURVEY_LAYER_CLASSIFICATION_KEYS.DEMAND_SMALL]: [0.8, 1.0],
      [SURVEY_LAYER_CLASSIFICATION_KEYS.DEMAND_OK]: [1.0, Number.MAX_VALUE],
    },
    [SURVEY_LAYERS.CALCIUM_AL]: {
      [SURVEY_LAYER_CLASSIFICATION_KEYS.DEMAND_LARGE]: [0.0, 60.0],
      [SURVEY_LAYER_CLASSIFICATION_KEYS.DEMAND_MEDIUM]: [60.0, 80.0],
      [SURVEY_LAYER_CLASSIFICATION_KEYS.DEMAND_SMALL]: [80.0, 100.0],
      [SURVEY_LAYER_CLASSIFICATION_KEYS.DEMAND_OK]: [100.0, Number.MAX_VALUE],
    },
    [SURVEY_LAYERS.PHOSPHORUS_HCL]: {
      [SURVEY_LAYER_CLASSIFICATION_KEYS.DEMAND_LARGE]: [0.0, 20.0],
      [SURVEY_LAYER_CLASSIFICATION_KEYS.DEMAND_MEDIUM]: [20.0, 40.0],
      [SURVEY_LAYER_CLASSIFICATION_KEYS.DEMAND_SMALL]: [40.0, 60.0],
      [SURVEY_LAYER_CLASSIFICATION_KEYS.DEMAND_OK]: [60.0, 80.0],
      [SURVEY_LAYER_CLASSIFICATION_KEYS.SURPLUS_SMALL]: [80.0, Number.MAX_VALUE],
    },
    [SURVEY_LAYERS.POTASSIUM_HCL]: {
      [SURVEY_LAYER_CLASSIFICATION_KEYS.DEMAND_LARGE]: [0.0, 50.0],
      [SURVEY_LAYER_CLASSIFICATION_KEYS.DEMAND_MEDIUM]: [50.0, 100.0],
      [SURVEY_LAYER_CLASSIFICATION_KEYS.DEMAND_SMALL]: [100.0, 200.0],
      [SURVEY_LAYER_CLASSIFICATION_KEYS.DEMAND_OK]: [200.0, 400.0],
      [SURVEY_LAYER_CLASSIFICATION_KEYS.SURPLUS_SMALL]: [400.0, Number.MAX_VALUE],
    },
    [SURVEY_LAYERS.POTASSIUM_MG]: {
      [SURVEY_LAYER_CLASSIFICATION_KEYS.DEMAND_LARGE]: [0.0, 0.5],
      [SURVEY_LAYER_CLASSIFICATION_KEYS.DEMAND_MEDIUM]: [0.5, 0.7],
      [SURVEY_LAYER_CLASSIFICATION_KEYS.DEMAND_SMALL]: [0.7, 1.5],
      [SURVEY_LAYER_CLASSIFICATION_KEYS.DEMAND_OK]: [1.5, 2.0],
      [SURVEY_LAYER_CLASSIFICATION_KEYS.SURPLUS_SMALL]: [2.0, 2.5],
      [SURVEY_LAYER_CLASSIFICATION_KEYS.SURPLUS_LARGE]: [2.5, Number.MAX_VALUE],
    },
  },
  [COUNTRY_CODES.ESTONIA]: {
    [SURVEY_LAYERS.ZINC]: {
      [SURVEY_LAYER_CLASSIFICATION_KEYS.DEMAND_LARGE]: [0.0, 0.2],
      [SURVEY_LAYER_CLASSIFICATION_KEYS.DEMAND_MEDIUM]: [0.2, 0.4],
      [SURVEY_LAYER_CLASSIFICATION_KEYS.DEMAND_SMALL]: [0.4, 0.75],
      [SURVEY_LAYER_CLASSIFICATION_KEYS.DEMAND_OK]: [0.75, 1.5],
      [SURVEY_LAYER_CLASSIFICATION_KEYS.SURPLUS_SMALL]: [1.5, 1.7],
      [SURVEY_LAYER_CLASSIFICATION_KEYS.SURPLUS_LARGE]: [1.7, 2.0],
      [SURVEY_LAYER_CLASSIFICATION_KEYS.SURPLUS_SUSPICIOUS]: [2.0, Number.MAX_VALUE],
    },
    [SURVEY_LAYERS.SULFUR]: {
      [SURVEY_LAYER_CLASSIFICATION_KEYS.DEMAND_LARGE]: [0.0, 5.0],
      [SURVEY_LAYER_CLASSIFICATION_KEYS.DEMAND_MEDIUM]: [5.0, 10.0],
      [SURVEY_LAYER_CLASSIFICATION_KEYS.DEMAND_SMALL]: [10.0, 20.0],
      [SURVEY_LAYER_CLASSIFICATION_KEYS.DEMAND_OK]: [20.0, 25.0],
      [SURVEY_LAYER_CLASSIFICATION_KEYS.SURPLUS_SMALL]: [25.0, 30.0],
      [SURVEY_LAYER_CLASSIFICATION_KEYS.SURPLUS_LARGE]: [30.0, 40.0],
      [SURVEY_LAYER_CLASSIFICATION_KEYS.SURPLUS_SUSPICIOUS]: [40.0, Number.MAX_VALUE],
    },
    [SURVEY_LAYERS.MOLYBDENUM]: {
      [SURVEY_LAYER_CLASSIFICATION_KEYS.DEMAND_LARGE]: [0.0, 0.02],
      [SURVEY_LAYER_CLASSIFICATION_KEYS.DEMAND_MEDIUM]: [0.02, 0.04],
      [SURVEY_LAYER_CLASSIFICATION_KEYS.DEMAND_SMALL]: [0.04, 0.05],
      [SURVEY_LAYER_CLASSIFICATION_KEYS.DEMAND_OK]: [0.05, 0.06],
      [SURVEY_LAYER_CLASSIFICATION_KEYS.SURPLUS_SMALL]: [0.06, 0.08],
      [SURVEY_LAYER_CLASSIFICATION_KEYS.SURPLUS_LARGE]: [0.08, 0.1],
      [SURVEY_LAYER_CLASSIFICATION_KEYS.SURPLUS_SUSPICIOUS]: [0.1, Number.MAX_VALUE],
    },
  },
  [COUNTRY_CODES.FINLAND]: {},
  [COUNTRY_CODES.LITHUANIA]: {
    [SURVEY_LAYERS.ZINC]: {
      [SURVEY_LAYER_CLASSIFICATION_KEYS.DEMAND_LARGE]: [0.0, 0.2],
      [SURVEY_LAYER_CLASSIFICATION_KEYS.DEMAND_MEDIUM]: [0.2, 0.4],
      [SURVEY_LAYER_CLASSIFICATION_KEYS.DEMAND_SMALL]: [0.4, 0.75],
      [SURVEY_LAYER_CLASSIFICATION_KEYS.DEMAND_OK]: [0.75, 1.5],
      [SURVEY_LAYER_CLASSIFICATION_KEYS.SURPLUS_SMALL]: [1.5, 1.7],
      [SURVEY_LAYER_CLASSIFICATION_KEYS.SURPLUS_LARGE]: [1.7, 2.0],
      [SURVEY_LAYER_CLASSIFICATION_KEYS.SURPLUS_SUSPICIOUS]: [2.0, Number.MAX_VALUE],
    },
    [SURVEY_LAYERS.SULFUR]: {
      [SURVEY_LAYER_CLASSIFICATION_KEYS.DEMAND_LARGE]: [0.0, 5.0],
      [SURVEY_LAYER_CLASSIFICATION_KEYS.DEMAND_MEDIUM]: [5.0, 10.0],
      [SURVEY_LAYER_CLASSIFICATION_KEYS.DEMAND_SMALL]: [10.0, 20.0],
      [SURVEY_LAYER_CLASSIFICATION_KEYS.DEMAND_OK]: [20.0, 25.0],
      [SURVEY_LAYER_CLASSIFICATION_KEYS.SURPLUS_SMALL]: [25.0, 30.0],
      [SURVEY_LAYER_CLASSIFICATION_KEYS.SURPLUS_LARGE]: [30.0, 40.0],
      [SURVEY_LAYER_CLASSIFICATION_KEYS.SURPLUS_SUSPICIOUS]: [40.0, Number.MAX_VALUE],
    },
    [SURVEY_LAYERS.MOLYBDENUM]: {
      [SURVEY_LAYER_CLASSIFICATION_KEYS.DEMAND_LARGE]: [0.0, 0.02],
      [SURVEY_LAYER_CLASSIFICATION_KEYS.DEMAND_MEDIUM]: [0.02, 0.04],
      [SURVEY_LAYER_CLASSIFICATION_KEYS.DEMAND_SMALL]: [0.04, 0.05],
      [SURVEY_LAYER_CLASSIFICATION_KEYS.DEMAND_OK]: [0.05, 0.06],
      [SURVEY_LAYER_CLASSIFICATION_KEYS.SURPLUS_SMALL]: [0.06, 0.08],
      [SURVEY_LAYER_CLASSIFICATION_KEYS.SURPLUS_LARGE]: [0.08, 0.1],
      [SURVEY_LAYER_CLASSIFICATION_KEYS.SURPLUS_SUSPICIOUS]: [0.1, Number.MAX_VALUE],
    },
  },
});

export const COUNTRY_LAYER_COLOR_INTERVAL_ADJUSTMENTS = Object.freeze({
  [COUNTRY_CODES.SWEDEN]: {
    [SURVEY_LAYERS.CLAY]: [
      [0.0, 5.0],
      [5.0, 15.0],
      [15.0, 25.0],
      [25.0, 40.0],
      [40.0, 60.0],
      [60.0, 80.0],
    ],
    [SURVEY_LAYERS.HUMUS]: [
      [0.0, 2.0],
      [2.0, 3.0],
      [3.0, 6.0],
      [6.0, 12.0],
      [12.0, 20.0],
      [20.0, 40.0],
      [40.0, Number.MAX_VALUE],
    ],
  },
  [COUNTRY_CODES.ESTONIA]: {
    [SURVEY_LAYERS.CLAY]: [
      [0.0, 5.0],
      [5.0, 10.0],
      [10.0, 15.0],
      [15.0, 25.0],
      [25.0, 45.0],
      [45.0, Number.MAX_VALUE],
    ],
    [SURVEY_LAYERS.OM]: [
      [0.0, 1.5],
      [1.5, 2.5],
      [2.5, 4.0],
      [4.0, 6.0],
      [6.0, 10.0],
      [10.0, 30.0],
      [30.0, Number.MAX_VALUE],
    ],
  },
  [COUNTRY_CODES.LITHUANIA]: {
    [SURVEY_LAYERS.CLAY]: [
      [0.0, 5.0],
      [5.0, 10.0],
      [10.0, 15.0],
      [15.0, 25.0],
      [25.0, 45.0],
      [45.0, Number.MAX_VALUE],
    ],
    [SURVEY_LAYERS.OM]: [
      [0.0, 1.5],
      [1.5, 2.5],
      [2.5, 4.0],
      [4.0, 6.0],
      [6.0, 10.0],
      [10.0, 30.0],
      [30.0, Number.MAX_VALUE],
    ],
  },
});

const buildCountryLayerDataRanges = (countryCode) => {
  const disabledLayers = COUNTRY_DISABLED_LAYERS[countryCode] || [];
  const base = filterByAllowedLayers(SURVEY_LAYER_DATA_RANGES, disabledLayers);
  const adjustments = COUNTRY_LAYER_DATA_RANGE_ADJUSTMENTS[countryCode] || {};
  return mergeDataRangeAdjustments(base, adjustments);
};

const buildCountryLayerClassifications = (countryCode) => {
  const disabledLayers = COUNTRY_DISABLED_LAYERS[countryCode] || [];
  const base = filterByAllowedLayers(
    cloneClassificationMap(SURVEY_LAYER_CLASSIFICATIONS),
    disabledLayers
  );
  const adjustments = COUNTRY_LAYER_CLASSIFICATION_ADJUSTMENTS[countryCode] || {};
  return mergeClassificationAdjustments(base, adjustments);
};

export const COUNTRY_LAYER_DATA_RANGES = Object.freeze({
  [COUNTRY_CODES.DENMARK]: buildCountryLayerDataRanges(COUNTRY_CODES.DENMARK),
  [COUNTRY_CODES.SWEDEN]: buildCountryLayerDataRanges(COUNTRY_CODES.SWEDEN),
  [COUNTRY_CODES.ESTONIA]: buildCountryLayerDataRanges(COUNTRY_CODES.ESTONIA),
  [COUNTRY_CODES.FINLAND]: buildCountryLayerDataRanges(COUNTRY_CODES.FINLAND),
  [COUNTRY_CODES.LITHUANIA]: buildCountryLayerDataRanges(COUNTRY_CODES.LITHUANIA),
});

export const COUNTRY_LAYER_CLASSIFICATIONS = Object.freeze({
  [COUNTRY_CODES.DENMARK]: buildCountryLayerClassifications(COUNTRY_CODES.DENMARK),
  [COUNTRY_CODES.SWEDEN]: buildCountryLayerClassifications(COUNTRY_CODES.SWEDEN),
  [COUNTRY_CODES.ESTONIA]: buildCountryLayerClassifications(COUNTRY_CODES.ESTONIA),
  [COUNTRY_CODES.FINLAND]: buildCountryLayerClassifications(COUNTRY_CODES.FINLAND),
  [COUNTRY_CODES.LITHUANIA]: buildCountryLayerClassifications(COUNTRY_CODES.LITHUANIA),
});

const COUNTRY_STRATEGIES = Object.freeze({
  [COUNTRY_CODES.DENMARK]: {
    countryCode: COUNTRY_CODES.DENMARK,
    soilClassLayer: SURVEY_LAYERS.JB,
    demandTypeByLayer: {
      [SURVEY_LAYERS.RT]: DEMAND_TYPES.DK,
      [SURVEY_LAYERS.PHOSPHORUS]: DEMAND_TYPES.DK,
      [SURVEY_LAYERS.POTASSIUM]: DEMAND_TYPES.DK,
      [SURVEY_LAYERS.MAGNESIUM]: DEMAND_TYPES.DK,
      [SURVEY_LAYERS.COPPER]: DEMAND_TYPES.DK,
      [SURVEY_LAYERS.BORON]: DEMAND_TYPES.DK,
      [SURVEY_LAYERS.ZINC]: DEMAND_TYPES.DK,
    },
    layerDataRanges: COUNTRY_LAYER_DATA_RANGES[COUNTRY_CODES.DENMARK],
    layerClassifications: COUNTRY_LAYER_CLASSIFICATIONS[COUNTRY_CODES.DENMARK],
    disabledLayers: COUNTRY_DISABLED_LAYERS[COUNTRY_CODES.DENMARK],
  },
  [COUNTRY_CODES.SWEDEN]: {
    countryCode: COUNTRY_CODES.SWEDEN,
    soilClassLayer: null,
    demandTypeByLayer: {},
    layerDataRanges: COUNTRY_LAYER_DATA_RANGES[COUNTRY_CODES.SWEDEN],
    layerClassifications: COUNTRY_LAYER_CLASSIFICATIONS[COUNTRY_CODES.SWEDEN],
    disabledLayers: COUNTRY_DISABLED_LAYERS[COUNTRY_CODES.SWEDEN],
  },
  [COUNTRY_CODES.ESTONIA]: {
    countryCode: COUNTRY_CODES.ESTONIA,
    soilClassLayer: SURVEY_LAYERS.EE_SOIL_CLASS,
    demandTypeByLayer: {
      [SURVEY_LAYERS.EE_SOIL_CLASS]: DEMAND_TYPES.EE,
      [SURVEY_LAYERS.PH]: DEMAND_TYPES.EE,
      [SURVEY_LAYERS.PHOSPHORUS]: DEMAND_TYPES.EE,
      [SURVEY_LAYERS.MAGNESIUM]: DEMAND_TYPES.EE,
      [SURVEY_LAYERS.BORON]: DEMAND_TYPES.EE,
      [SURVEY_LAYERS.COPPER]: DEMAND_TYPES.EE,
      [SURVEY_LAYERS.POTASSIUM]: DEMAND_TYPES.EE,
      [SURVEY_LAYERS.MANGANESE]: DEMAND_TYPES.EE,
      [SURVEY_LAYERS.MOLYBDENUM]: DEMAND_TYPES.EE,
      [SURVEY_LAYERS.SULFUR]: DEMAND_TYPES.EE,
      [SURVEY_LAYERS.ZINC]: DEMAND_TYPES.EE,
    },
    layerDataRanges: COUNTRY_LAYER_DATA_RANGES[COUNTRY_CODES.ESTONIA],
    layerClassifications: COUNTRY_LAYER_CLASSIFICATIONS[COUNTRY_CODES.ESTONIA],
    disabledLayers: COUNTRY_DISABLED_LAYERS[COUNTRY_CODES.ESTONIA],
  },
  [COUNTRY_CODES.FINLAND]: {
    countryCode: COUNTRY_CODES.FINLAND,
    soilClassLayer: SURVEY_LAYERS.FI_SOIL_CLASS,
    demandTypeByLayer: {
      [SURVEY_LAYERS.FI_SOIL_CLASS]: DEMAND_TYPES.FI,
    },
    layerDataRanges: COUNTRY_LAYER_DATA_RANGES[COUNTRY_CODES.FINLAND],
    layerClassifications: COUNTRY_LAYER_CLASSIFICATIONS[COUNTRY_CODES.FINLAND],
    disabledLayers: COUNTRY_DISABLED_LAYERS[COUNTRY_CODES.FINLAND],
  },
  [COUNTRY_CODES.LITHUANIA]: {
    countryCode: COUNTRY_CODES.LITHUANIA,
    soilClassLayer: SURVEY_LAYERS.LT_SOIL_CLASS,
    demandTypeByLayer: {
      [SURVEY_LAYERS.LT_SOIL_CLASS]: DEMAND_TYPES.LT,
      [SURVEY_LAYERS.PH]: DEMAND_TYPES.LT,
      [SURVEY_LAYERS.PHOSPHORUS]: DEMAND_TYPES.LT,
      [SURVEY_LAYERS.MAGNESIUM]: DEMAND_TYPES.LT,
      [SURVEY_LAYERS.BORON]: DEMAND_TYPES.LT,
      [SURVEY_LAYERS.COPPER]: DEMAND_TYPES.LT,
      [SURVEY_LAYERS.POTASSIUM]: DEMAND_TYPES.LT,
      [SURVEY_LAYERS.MANGANESE]: DEMAND_TYPES.LT,
      [SURVEY_LAYERS.MOLYBDENUM]: DEMAND_TYPES.LT,
      [SURVEY_LAYERS.SULFUR]: DEMAND_TYPES.LT,
      [SURVEY_LAYERS.ZINC]: DEMAND_TYPES.LT,
    },
    layerDataRanges: COUNTRY_LAYER_DATA_RANGES[COUNTRY_CODES.LITHUANIA],
    layerClassifications: COUNTRY_LAYER_CLASSIFICATIONS[COUNTRY_CODES.LITHUANIA],
    disabledLayers: COUNTRY_DISABLED_LAYERS[COUNTRY_CODES.LITHUANIA],
  },
});

const DEFAULT_COUNTRY_STRATEGY = Object.freeze({
  countryCode: 'DEFAULT',
  soilClassLayer: null,
  demandTypeByLayer: {},
  layerDataRanges: SURVEY_LAYER_DATA_RANGES,
  layerClassifications: SURVEY_LAYER_CLASSIFICATIONS,
  disabledLayers: [],
});

const DEFAULT_DEMAND_TYPE_BY_LAYER = Object.freeze({
  [SURVEY_LAYERS.FI_SOIL_CLASS]: DEMAND_TYPES.FI,
  [SURVEY_LAYERS.EE_SOIL_CLASS]: DEMAND_TYPES.EE,
  [SURVEY_LAYERS.LT_SOIL_CLASS]: DEMAND_TYPES.LT,
});

const DEFAULT_SOIL_CLASS_DEFINITION_BY_LAYER = Object.freeze({
  [SURVEY_LAYERS.JB]: {
    colorMap: SURVEY_LAYER_SOIL_TYPE_COLORS,
    showLabel: false,
  },
  [SURVEY_LAYERS.FI_SOIL_CLASS]: {
    soilTypes: soilTypesFI,
    showLabel: true,
  },
  [SURVEY_LAYERS.EE_SOIL_CLASS]: {
    soilTypes: soilTypesEE,
    showLabel: true,
  },
  [SURVEY_LAYERS.LT_SOIL_CLASS]: {
    soilTypes: soilTypesLT,
    showLabel: true,
  },
});

const DEFAULT_CLASSIFICATION_IMAGE_ALIASES = Object.freeze({
  [DEMAND_TYPES.FI]: {
    coarse: ['FI_DEMAND_COARSE'],
    fine: ['FI_DEMAND_FINE'],
  },
  [DEMAND_TYPES.EE]: {
    coarse: ['EE_DEMAND_COARSE'],
    fine: ['EE_DEMAND_FINE'],
  },
});

export function normalizeCountryCode(input) {
  if (!input) {
    return null;
  }

  const normalized = String(input).trim().toUpperCase();
  return COUNTRY_NAME_TO_CODE[normalized] || null;
}

export function resolveSurveyCountryCodeFromFarm(farm) {
  if (!farm || !farm.country) {
    return null;
  }
  return normalizeCountryCode(farm.country);
}

export function resolveSurveyCountryCodeFromSurveysPayload(payload) {
  if (!payload) {
    return null;
  }
  return normalizeCountryCode(payload.country);
}

export function setActiveSurveyCountryCode(countryCode) {
  activeSurveyCountryCode = normalizeCountryCode(countryCode);
}

export function getActiveSurveyCountryCode() {
  return activeSurveyCountryCode;
}

export function getSurveyCountryStrategy(countryCode) {
  const normalized = normalizeCountryCode(countryCode);
  if (normalized && COUNTRY_STRATEGIES[normalized]) {
    return COUNTRY_STRATEGIES[normalized];
  }
  return DEFAULT_COUNTRY_STRATEGY;
}

export function getActiveSurveyCountryStrategy() {
  return getSurveyCountryStrategy(getActiveSurveyCountryCode());
}

export function isSurveyLayerEnabledInCountry(layer, countryCode) {
  const strategy = getSurveyCountryStrategy(countryCode);
  return !strategy.disabledLayers.includes(layer);
}

export function getSurveyLayerDataRange(layer, countryCode) {
  const strategy = getSurveyCountryStrategy(countryCode);
  return strategy.layerDataRanges[layer] || SURVEY_LAYER_DATA_RANGES[layer];
}

export function getSurveyLayerClassifications(layer, countryCode) {
  const strategy = getSurveyCountryStrategy(countryCode);
  return strategy.layerClassifications[layer] || SURVEY_LAYER_CLASSIFICATIONS[layer];
}

// True only when the country provides explicit one-dimensional classification
// thresholds for the layer (i.e. an entry exists in
// COUNTRY_LAYER_CLASSIFICATION_ADJUSTMENTS[countryCode]). Multi-dimensional
// (soil-type-dependent) classifications have no single numeric range and
// therefore return false here.
export function hasCountrySpecificLayerClassifications(layer, countryCode) {
  const normalized = normalizeCountryCode(countryCode);
  if (!normalized) {
    return false;
  }
  const adjustments = COUNTRY_LAYER_CLASSIFICATION_ADJUSTMENTS[normalized];
  return Boolean(adjustments && adjustments[layer]);
}

export function getSurveyLayerColorIntervals(layer, baseIntervals, countryCode) {
  const normalized = normalizeCountryCode(countryCode);
  const adjustments =
    (normalized &&
      COUNTRY_LAYER_COLOR_INTERVAL_ADJUSTMENTS[normalized] &&
      COUNTRY_LAYER_COLOR_INTERVAL_ADJUSTMENTS[normalized][layer]) ||
    null;

  if (!adjustments) {
    return baseIntervals;
  }

  return mapDiscreteIntervalsByColor(baseIntervals, adjustments);
}

export function isSoilClassLayer(layer) {
  return SURVEY_SOIL_CLASS_LAYERS.includes(layer);
}

export function getDemandTypeForLayer(layer, countryCode) {
  const strategy = getSurveyCountryStrategy(countryCode);
  return strategy.demandTypeByLayer[layer] || DEFAULT_DEMAND_TYPE_BY_LAYER[layer] || null;
}

export function isCustomClassificationLayer(layer, countryCode) {
  return getDemandTypeForLayer(layer, countryCode) != null;
}

export function getDemandTypeFromLayerType(layerType) {
  if (
    layerType === DEMAND_TYPES.FI ||
    layerType === DEMAND_TYPES.EE ||
    layerType === DEMAND_TYPES.DK
  ) {
    return layerType;
  }
  return null;
}

export function isDemandLayerType(layerType) {
  return Boolean(getDemandTypeFromLayerType(layerType));
}

export function getSoilClassDefinition(layer) {
  return DEFAULT_SOIL_CLASS_DEFINITION_BY_LAYER[layer] || null;
}

export function hasSoilClassLabels(layer) {
  const config = getSoilClassDefinition(layer);
  return Boolean(config && config.showLabel);
}

export function shouldIncludeZeroForLayerMinMax(layer, countryCode) {
  if (!isSoilClassLayer(layer)) {
    return false;
  }

  const range = getSurveyLayerDataRange(layer, countryCode);
  return Boolean(range && range[0] <= 0);
}

export function getClassificationImageAliases(layer, countryCode) {
  const demandType = getDemandTypeForLayer(layer, countryCode);
  if (!demandType) {
    return { coarse: [], fine: [] };
  }

  return DEFAULT_CLASSIFICATION_IMAGE_ALIASES[demandType] || { coarse: [], fine: [] };
}

export function getClassificationImageAliasKeys(layer, countryCode, mode) {
  const aliases = getClassificationImageAliases(layer, countryCode);
  return mode === 'OVERVIEW' ? aliases.coarse : aliases.fine;
}
